/**
 * CSS Custom Property (variable) definitions for the design token system.
 *
 * Injected into :root via global-styles.ts. Consumed as var(--color-*)
 * in styled-components. Values are 1:1 with the existing color palette
 * so this migration is zero-visual-change.
 */
const cssTokens = `
  :root {
    /* ── Surface / Background ── */
    --color-bg-primary: #FFFFFF;
    --color-bg-secondary: #FBFBFB;
    --color-bg-tertiary: #F6F6F6;
    --color-bg-sunken: #F0F0F0;
    --color-bg-hover: #EEEFF1;
    --color-bg-inverse: #171717;
    --color-bg-frosted: #FAFBFC;
    --color-bg-soft: #F4F5F7;
    --color-bg-smoke: #F8F8F8;
    --color-bg-selected: #E5F0FA;
    --color-bg-input: #F2F2F2;
    --color-bg-tag: #EBEBEB;
    --color-bg-muted: #FBFBFE;
    --color-bg-search-highlight: #E7E4F7;
    --color-bg-static-white: #FFFFFF;
    /* Cards and gadgets sitting on a page */
    --color-bg-raised: #FFFFFF;
    /* Studio editing canvas, recessed behind its blocks */
    --color-bg-canvas: #FBFBFB;
    /* A Text card's tile: inverted (dark on a light page) or a soft shade */
    --color-text-card-inverted-bg: #171717;
    --color-text-card-inverted-text: #FFFFFF;
    --color-text-card-subtle-bg: #F4F5F7;
    --color-text-card-subtle-text: #1A1919;

    /* ── Text ── */
    --color-text-primary: #1A1919;
    --color-text-secondary: #333333;
    --color-text-tertiary: #808080;
    --color-text-muted: #8E8E8E;
    --color-text-disabled: #C3C3C3;
    --color-text-inverse: #FFFFFF;
    /* Secondary text on an inverse background (e.g. counts on a selected pill) */
    --color-text-inverse-secondary: #A3A3A3;
    --color-text-link: #0041EA;
    --color-text-link-hover: #1275F7;

    /* ── Border ── */
    --color-border-primary: #E0E0E0;
    --color-border-secondary: #EEEFF1;
    --color-border-tertiary: #EDEDED;
    --color-border-focus: #0041EA;

    /* ── Icon ── */
    --color-icon-primary: #1A1919;
    --color-icon-secondary: #808080;
    --color-icon-tertiary: #C3C3C3;

    /* ── Status / Semantic ── */
    --color-status-info: #0041EA;
    --color-status-success: #36B37E;
    --color-status-warning: #F3C25F;
    /* Text on a warning fill, which is the same yellow in both modes */
    --color-status-warning-text: #333333;
    /* A pale warning surface, e.g. a banner, under primary text */
    --color-status-warning-bg: #FFF8E6;
    --color-status-error: #D66969;
    --color-status-error-bg: #FFF2F8;
    --color-status-danger: #FF3366;
    --color-status-danger-bg: #FDF2F2;
    /* A solid destructive fill, e.g. a Danger button, under white text.
       The same red in both modes. */
    --color-status-danger-solid: #DC2626;
    --color-status-danger-solid-hover: #B91C1C;
    --color-status-danger-solid-text: #FFFFFF;
    --color-status-success-bg: #EBFAE2;
    --color-status-success-transparent: #36B37E20;
    --color-status-danger-transparent: #FF336620;
    --color-status-orange: #FF5630;
    --color-status-amber: #FFBF00;

    /* Semantic state colors */
    --color-success-bg: #EBFAE2;
    --color-success-border: #D2F0C0;
    --color-danger-bg: #FDF2F2;
    --color-danger-border: #F8B0B4;

    /* ── Brand ── */
    --color-brand-primary: #DF3E56;
    --color-brand-primary-alt: #DF3D55;
    --color-brand-dark: #352D43;
    --color-brand-dark-alt: #3F384E;
    --color-brand-accent: #6264A7;
    --color-brand-saturated: #E2DBF2;

    /* ── AI / Insights accent ── */
    --color-ai-accent: #6264A7;
    --color-ai-accent-hover: #4F5194;
    --color-ai-accent-subtle: rgba(98, 100, 167, 0.14);
    /* Colourful gradient used for the Insights button border. */
    --color-ai-gradient: linear-gradient(120deg, #6264a7, #a855f7, #38bdf8);

    /* ── Loading Spinner ── */
    --color-spinner-grouped: #c5cbff;
    --color-spinner-default: #e8eaff;

    /* ── Skeleton (loading placeholders) ── */
    --color-skeleton-base: #E8EAED;
    --color-skeleton-highlight: #F4F5F7;

    /* ── Shadows ── */
    --shadow-sm: 0px 3px 2px -1px rgba(0,0,0,0.02), 0px 1px 1px -1px rgba(0,0,0,0.04);
    --shadow-md: 0px 4px 8px -4px rgba(28,40,64,0.12), 0px 4px 12px -2px rgba(28,40,64,0.16);
    --shadow-lg: 0 16px 40px rgba(0,0,0,0.22), 0 4px 14px rgba(0,0,0,0.14);
    --shadow-xs: 0px 0px 2px 0px rgba(0, 0, 0, 0.15);
    --shadow-inset: inset 0px -1px 0px 0px rgba(0, 0, 0, 0.08);
    --shadow-border: 0px 3px 8px 0px rgba(0, 0, 0, 0.04);
    --shadow-dropdown: 0px 2px 8px 0px rgba(63, 63, 68, 0.12);
    --shadow-tooltip: 0px 2px 4px 0px rgba(0, 0, 0, 0.18);
    --shadow-floating-button: 0px 2px 6px rgba(0, 0, 0, 0.08);
    --shadow-overlay: rgba(0, 0, 0, 0.3);

    /* ── Tooltip ── */
    --color-tooltip-bg: #171717;
    --color-tooltip-text: #FFFFFF;
    --color-tooltip-text-muted: #ABABAB;
    --color-tooltip-section-bg: #2A2A2A;
    --color-tooltip-row-active-bg: #363636;
    --color-tooltip-divider: #3E3E3E;
    --color-tooltip-positive: #4CC990;
    --color-tooltip-positive-bg: #173A2A;

    /* ── Calendar ── */
    --color-calendar-range-bg: #F4F5F7;
    --color-calendar-range-text: #2B3238;
    --color-calendar-selected-bg: #DF3D55;
    --color-calendar-selected-text: #FFFFFF;

    /* ── Pill ── */
    --color-pill-selected-bg: #FFFFFF;
    --color-pill-selected-text: #1A1919;

    /* ── Grid ── */
    --color-grid-odd-row: #F7F9FB;
    --color-grid-odd-row-hover: #EEEFF0;
    --color-grid-even-row-hover: #F4F5F6;
    --color-grid-border: #F0F0F0;
    --color-grid-border-alt: #D8D8D8;
    --color-grid-border-row: #E5E5F2;
    --color-grid-selected-row: #E5F0FA;
    --color-grid-header-text: #13171B;
    --color-grid-cell-text: #2B3238;
    --color-grid-loading-overlay: rgba(0, 0, 0, 0.05);

    /* ── Badge ── */
    --color-badge-info-bg: #F7F9FF;
    --color-badge-info-border: #BBCCFF;
    --color-badge-warning-bg: #FFF2E4;
    --color-badge-warning-border: #FFC382;
    --color-badge-danger-bg: #FDF2F2;
    --color-badge-danger-border: #F8B0B4;
    --color-badge-success-bg: #EBFAE2;
    --color-badge-success-border: #D2F0C0;

    /* Badge type-specific colors */
    --color-badge-blue-bg: #EFF6FF;
    --color-badge-blue-border: #D9E4FC;
    --color-badge-blue-text: #1447E6;

    --color-badge-turquoise-bg: #F0F8FD;
    --color-badge-turquoise-border: #C0E4EC;
    --color-badge-turquoise-text: #008275;

    --color-badge-green-bg: #F0FDF4;
    --color-badge-green-border: #C0ECD0;
    --color-badge-green-text: #008236;

    --color-badge-olive-bg: #FBFDF0;
    --color-badge-olive-border: #E6ECC0;
    --color-badge-olive-text: #488200;

    --color-badge-orange-bg: #FFF2E4;
    --color-badge-orange-border: #FFC382;
    --color-badge-orange-text: #9C602E;

    --color-badge-yellow-bg: #FEFCE8;
    --color-badge-yellow-border: #F5E5BA;
    --color-badge-yellow-text: #894B00;

    --color-badge-red-bg: #FEF2F2;
    --color-badge-red-border: #FBD9DA;
    --color-badge-red-text: #C10007;

    --color-badge-purple-bg: #EEF2FF;
    --color-badge-purple-border: #DDDEFB;
    --color-badge-purple-text: #432DD7;

    --color-badge-magenta-bg: #FAF5FF;
    --color-badge-magenta-border: #EEDCFB;
    --color-badge-magenta-text: #8200DB;

    --color-badge-pink-bg: #FDF2F8;
    --color-badge-pink-border: #F8DAE8;
    --color-badge-pink-text: #C6005C;

    /* ── Colors2 Grey Scale ── */
    --color-grey-1: #1E1A26;
    --color-grey-2: #5C5C5C;
    --color-grey-3: #828282;
    --color-grey-4: #919191;
    --color-grey-5: #A1A1A1;
    --color-grey-7: #DCDCDC;
    --color-grey-8: #E9E9E9;
    --color-grey-9: #FBFBFE;

    /* ── Legacy Secondary Colors ── */
    --color-info-v2: #1252F7;
    --color-success-v2: #6FBD84;

    /* ── Legacy Accent Colors ── */
    --color-gun-metal: #2B3238;
    --color-logan: #97A0AF;
    --color-navy: #016CD1;
    --color-dodger-blue: #24A0FC;
    --color-shuttle-grey: #5C636D;
    --color-shady-lady: #979797;

    /* ── Legacy Border / Stroke ── */
    --color-gainsboro: #E5E5E5;
    --color-saturated-zircon: #E0E3E7;
    --color-stroke-light: #E1DFEA;
    --color-teams-close: #CCD0D6;

    /* ── Legacy Navigation ── */
    --color-nav-bg: #3F364E;
    --color-nav-text: #3F354E;
    --color-nav-icon: #71687F;
    --color-nav-text-light: #A99CC0;
    --color-nav-grey: #E9EAEC;

    /* ── Avatar Colors ── */
    --color-avatar-1-bg: #E7E4F7;
    --color-avatar-1-text: #6859B5;
    --color-avatar-2-bg: #D4E6FF;
    --color-avatar-2-text: #1D63Be;
    --color-avatar-3-bg: #FFF3E3;
    --color-avatar-3-text: #9B5C03;
    --color-avatar-4-bg: #E7E9ED;
    --color-avatar-4-text: #515D72;
    --color-avatar-5-bg: #DDF8F1;
    --color-avatar-5-text: #06795C;
    --color-avatar-6-bg: #DAF5FB;
    --color-avatar-6-text: #147286;
    --color-avatar-7-bg: #FEEEF4;
    --color-avatar-7-text: #AB4367;
    --color-avatar-8-bg: #FDE9E9;
    --color-avatar-8-text: #C9181E;

    /* ── Category Gradient Colors ── */
    --color-category-blue-start: #6da6e8;
    --color-category-blue-end: #a7cffe;
    --color-category-green-start: #74c283;
    --color-category-green-end: #9be3a7;
    --color-category-yellow-start: #f9d948;
    --color-category-yellow-end: #f6c252;
    --color-category-orange-start: #f5943f;
    --color-category-orange-end: #ffb777;
    --color-category-red-start: #f66c6c;
    --color-category-red-end: #ffa0a0;
    --color-category-pink-start: #ef8ab8;
    --color-category-pink-end: #ffb2d4;
    --color-category-purple-start: #696cd4;
    --color-category-purple-end: #9ea0ff;
    --color-category-cyan-start: #00fff7;
    --color-category-cyan-end: #8ae8e4;

    /* ── Studio canvas ── */
    /* The entity Studio previews as, among the top bar controls */
    --color-studio-preview-chip-bg: #EEEEF8;
    --color-studio-preview-chip-border: #B4B5DD;
    /* Panel edges and bars, the same line as the top bar's (--shadow-inset) */
    --color-studio-divider: rgba(0, 0, 0, 0.08);
    --color-studio-cell-outline: rgba(26, 25, 25, 0.14);
    --color-studio-canvas-border: rgba(26, 25, 25, 0.06);
    /* Around the canvas's frame, a shade under it */
    --color-studio-canvas-surround: #F3F3F3;
    --color-studio-toggle-track: #E7E8EA;
    --color-studio-grid-dot: rgba(26, 25, 25, 0.14);
    --color-studio-drop-target: rgba(26, 25, 25, 0.08);
    /* Where a block being dragged onto the grid would land */
    --color-studio-drop-preview-bg: rgba(123, 147, 247, 0.06);
    --color-studio-drop-preview-border: #7B93F7;
    --color-studio-drop-preview-halo: rgba(123, 147, 247, 0.14);
    /* A Grouped block's dashed "+" spots a drag could go in: filled, the one
       it's over more so, with a darker "+" */
    --color-studio-drop-spot-bg: rgba(123, 147, 247, 0.16);
    --color-studio-drop-spot-bg-active: rgba(123, 147, 247, 0.32);
    --color-studio-drop-spot-icon: #3F5BD9;
    /* The shimmer across a block just added from the library */
    --color-studio-reveal-shimmer: rgba(123, 147, 247, 0.22);
    /* A selected block's outline and resize handles, and their hover hint */
    --color-studio-selection: #7B93F7;
    --color-studio-selection-subtle: rgba(123, 147, 247, 0.45);
    /* The drag tab over a hovered block that isn't selected */
    --color-studio-drag-chip-bg: #5A5A63;
    --color-studio-drag-chip-text: #FFFFFF;
    /* A Grouped block's row under the pointer */
    --color-studio-row-hover-bg: rgba(123, 147, 247, 0.04);
    /* The gadget type palette's two-tone icons, and their emptiest heatmap cell */
    --color-studio-vis-icon-accent: #3451C7;
    --color-studio-vis-icon-soft: #C3CEF6;
    --color-studio-vis-icon-muted: #D5D9E0;
    /* The library's gadget types and metrics, raised like chips: shaded down
       to a darker lower edge, a sheen along the top and a shadow that lifts
       on hover */
    --color-studio-chip-shade: #F4F5F7;
    --color-studio-chip-edge: #C8CCD3;
    --color-studio-chip-sheen: rgba(255, 255, 255, 0.9);
    --shadow-studio-chip: 0 1px 2px rgba(28, 40, 64, 0.08);
    --shadow-studio-chip-lifted: 0 4px 10px -2px rgba(28, 40, 64, 0.16), 0 1px 2px rgba(28, 40, 64, 0.08);
    /* A Studio block held while it's dragged */
    --shadow-studio-block-lifted: 0 16px 32px -8px rgba(28, 40, 64, 0.28), 0 4px 12px -2px rgba(28, 40, 64, 0.12);
    /* The settings panel's section titles: a slate grey overline */
    --color-studio-section-title: #5F6470;
    /* A setting the gadget still needs (e.g. its first metric): a tinted
       drop zone with a dashed edge */
    --color-studio-required-bg: #F2F4FD;
    --color-studio-required-bg-hover: #E8ECFC;
    --color-studio-required-border: #B3BDE6;
    --color-studio-required-text: #3451C7;
    /* A settings section appearing in place (e.g. a chart's dimensions,
       once it has a metric): pulsed once, so the eye lands on it */
    --color-studio-section-reveal: #DCE3FB;
    /* A panel's link out to a modal (e.g. View Metric Library): a full
       width bar, shaded darker on hover */
    --color-studio-modal-link-bg-hover: rgba(26, 25, 25, 0.08);
    /* The settings panel's Advanced button: link blue, tinted blue on hover */
    --color-studio-advanced-bg-hover: rgba(18, 82, 247, 0.08);
    --color-studio-advanced-bg-active: rgba(18, 82, 247, 0.14);
    --color-studio-advanced-focus: rgba(18, 82, 247, 0.4);

    /* ── Scorecard ── */
    --color-scorecard-green-desaturated: #E0FFE8;
    --color-scorecard-green: #A6F4C5;
    --color-scorecard-red-desaturated: #FFEAEA;
    --color-scorecard-red: #FECDCA;
    --color-scorecard-amber: #FFF6E4;
    --color-scorecard-green-text: #05603A;
    --color-scorecard-amber-text: #7A4807;
    --color-scorecard-red-text: #AD261D;
    --color-scorecard-grey-text: #414141;
    --color-scorecard-black-text: #000000;
    --color-scorecard-green-desaturated-chart: #a5f2c3;
    --color-scorecard-green-chart: #79d79e;
    --color-scorecard-red-desaturated-chart: #ffb0ac;
    --color-scorecard-red-chart: #f28b86;
    --color-scorecard-amber-chart: #fce090;

    /* ── User Icon (17 original avatar colors) ── */
    --color-usericon-0-bg: #d0c6e2;
    --color-usericon-0-text: #2b3238;
    --color-usericon-1-bg: #b5e4f6;
    --color-usericon-1-text: #2b3238;
    --color-usericon-2-bg: #ffec96;
    --color-usericon-2-text: #2b3238;
    --color-usericon-3-bg: #cbeed2;
    --color-usericon-3-text: #2b3238;
    --color-usericon-4-bg: #90b097;
    --color-usericon-4-text: #2b3238;
    --color-usericon-5-bg: #f8afba;
    --color-usericon-5-text: #2b3238;
    --color-usericon-6-bg: #00C7E6;
    --color-usericon-6-text: #2b3238;
    --color-usericon-7-bg: #2684FF;
    --color-usericon-7-text: #000000;
    --color-usericon-8-bg: #8777D9;
    --color-usericon-8-text: #000000;
    --color-usericon-9-bg: #5E6C84;
    --color-usericon-9-text: #ffffff;
    --color-usericon-10-bg: #FF8F73;
    --color-usericon-10-text: #2b3238;
    --color-usericon-11-bg: #FFE380;
    --color-usericon-11-text: #2b3238;
    --color-usericon-12-bg: #79F2C0;
    --color-usericon-12-text: #2b3238;
    --color-usericon-13-bg: #79E2F2;
    --color-usericon-13-text: #2b3238;
    --color-usericon-14-bg: #4C9AFF;
    --color-usericon-14-text: #2b3238;
    --color-usericon-15-bg: #998DD9;
    --color-usericon-15-text: #000000;
    --color-usericon-16-bg: #7A869A;
    --color-usericon-16-text: #000000;

    /* ── Chart Colors (57 colors for data visualization) ── */
    --color-chart-1: #a7abea;
    --color-chart-2: #EAB16D;
    --color-chart-3: #7AB1D2;
    --color-chart-4: #EAD659;
    --color-chart-5: #6DDC8F;
    --color-chart-6: #EA962E;
    --color-chart-7: #A9D346;
    --color-chart-8: #EA60A8;
    --color-chart-9: #B0C95C;
    --color-chart-10: #C04FEA;
    --color-chart-11: #EA3E43;
    --color-chart-12: #194B3D;
    --color-chart-13: #191939;
    --color-chart-14: #1F5D4F;
    --color-chart-15: #224C19;
    --color-chart-16: #5C1F35;
    --color-chart-17: #C55259;
    --color-chart-18: #E05A00;
    --color-chart-19: #5364A7;
    --color-chart-20: #295D1F;
    --color-chart-21: #55C757;
    --color-chart-22: #1BBE95;
    --color-chart-23: #56C7BE;
    --color-chart-24: #673C18;
    --color-chart-25: #141E95;
    --color-chart-26: #1F306E;
    --color-chart-27: #4A1942;
    --color-chart-28: #6455C6;
    --color-chart-29: #C654A4;
    --color-chart-30: #5D501F;
    --color-chart-31: #C7B656;
    --color-chart-32: #48181B;
    --color-chart-33: #BA2020;
    --color-chart-34: #472518;
    --color-chart-35: #5D251F;
    --color-chart-36: #FFA70B;
    --color-chart-37: #F0D632;
    /* Palettes: Macaron (38-42), Pastel (43-47), Dusk (48-52), Meadow (53-57) */
    --color-chart-38: #2EC7C9;
    --color-chart-39: #B6A2DE;
    --color-chart-40: #5AB1EF;
    --color-chart-41: #FFB980;
    --color-chart-42: #D87A80;
    --color-chart-43: #FC97AF;
    --color-chart-44: #87F7CF;
    --color-chart-45: #F7F494;
    --color-chart-46: #72CCFF;
    --color-chart-47: #F7C5A0;
    --color-chart-48: #516B91;
    --color-chart-49: #59C4E6;
    --color-chart-50: #EDAFDA;
    --color-chart-51: #93B7E3;
    --color-chart-52: #A5E7F0;
    --color-chart-53: #3FB1E3;
    --color-chart-54: #6BE6C1;
    --color-chart-55: #626C91;
    --color-chart-56: #A0A7E6;
    --color-chart-57: #C4EBAD;

    /* ── Driver Leaderboard wallboard (design handoff) ── */
    --color-leaderboard-surface: #ffffff;
    --color-leaderboard-header-bg: #f6f6f6;
    --color-leaderboard-border: #e0e0e0;
    --color-leaderboard-row-border: #ededed;
    --color-leaderboard-ink: #1a1919;
    --color-leaderboard-header-label: #4a4d52;
    --color-leaderboard-muted: #737373;
    --color-leaderboard-medal-gold: #c68b3a;
    --color-leaderboard-medal-silver: #8a8e94;
    --color-leaderboard-medal-bronze: #b5523f;
    --color-leaderboard-tint-gold: #fbf1e1;
    --color-leaderboard-tint-silver: #f2f3f5;
    --color-leaderboard-tint-bronze: #f9eeeb;
    --color-leaderboard-up: #15642d;
    --color-leaderboard-down: #c92434;
    --color-leaderboard-down-arrow: #e66b6b;
    --color-leaderboard-new: #1f3f8a;
    --color-leaderboard-quiet: #c3c3c3;
    --color-leaderboard-progress: #000000;
    --color-leaderboard-freshness: #a3a3a3;
    --color-leaderboard-secondary: #5c5c5c;
    --color-leaderboard-row-stripe: #fafafa;
  }
`;

export const darkCssTokens = `
  html[data-theme="dark"] {
    /* ── Surface / Background ── */
    --color-bg-primary: #1F252C;
    --color-bg-secondary: #252E39;
    --color-bg-tertiary: #2C3643;
    --color-bg-sunken: #1A1F25;
    --color-bg-hover: #333E4C;
    --color-bg-inverse: #F0F0F0;
    --color-bg-frosted: #222A33;
    --color-bg-soft: #2C3643;
    --color-bg-smoke: #212831;
    --color-bg-selected: #1E3A5C;
    --color-bg-input: #2A3440;
    --color-bg-tag: #3A4656;
    --color-bg-muted: #232A34;
    --color-bg-search-highlight: #4A3D8F;
    --color-bg-static-white: #FFFFFF;
    /* Cards and gadgets sitting on a page */
    --color-bg-raised: #252E39;
    /* Studio editing canvas, recessed behind its blocks */
    --color-bg-canvas: #1A1F25;
    /* A Text card's tile: inverted (light on a dark page) or a soft shade */
    --color-text-card-inverted-bg: #F0F0F0;
    --color-text-card-inverted-text: #1A1919;
    --color-text-card-subtle-bg: #313B47;
    --color-text-card-subtle-text: #E8E8E8;

    /* ── Text ── */
    --color-text-primary: #E8E8E8;
    --color-text-secondary: #CCCCCC;
    --color-text-tertiary: #999999;
    --color-text-muted: #888888;
    --color-text-disabled: #555555;
    --color-text-inverse: #1A1919;
    /* Secondary text on an inverse background (e.g. counts on a selected pill) */
    --color-text-inverse-secondary: #6B6B6B;
    --color-text-link: #6B8FFF;
    --color-text-link-hover: #89ABFF;

    /* ── Border ── */
    --color-border-primary: #36414F;
    --color-border-secondary: #2C3643;
    --color-border-tertiary: #313B48;
    --color-border-focus: #6B8FFF;

    /* ── Icon ── */
    --color-icon-primary: #E0E0E0;
    --color-icon-secondary: #999999;
    --color-icon-tertiary: #555555;

    /* ── Status / Semantic ── */
    --color-status-info: #6B8FFF;
    --color-status-success: #4CC990;
    --color-status-warning: #F3C25F;
    /* Text on a warning fill, which is the same yellow in both modes */
    --color-status-warning-text: #333333;
    /* A pale warning surface, e.g. a banner, under primary text */
    --color-status-warning-bg: #3A3118;
    --color-status-error: #E07070;
    --color-status-error-bg: #3A1A28;
    --color-status-danger: #FF4477;
    --color-status-danger-bg: #3A1A1A;
    /* A solid destructive fill, e.g. a Danger button, under white text.
       The same red in both modes. */
    --color-status-danger-solid: #DC2626;
    --color-status-danger-solid-hover: #B91C1C;
    --color-status-danger-solid-text: #FFFFFF;
    --color-status-success-bg: #1A3A1A;
    --color-status-success-transparent: #36B37E20;
    --color-status-danger-transparent: #FF336620;
    --color-status-orange: #FF6B45;
    --color-status-amber: #FFBF00;

    /* Semantic state colors */
    --color-success-bg: #1A3A1A;
    --color-success-border: #2A4A2A;
    --color-danger-bg: #3A1A1A;
    --color-danger-border: #5A2A2A;

    /* ── Brand ── */
    --color-brand-primary: #DF3E56;
    --color-brand-primary-alt: #DF3D55;
    --color-brand-dark: #E0E0E0;
    --color-brand-dark-alt: #252030;
    --color-brand-accent: #7B7DBA;
    --color-brand-saturated: #2A2540;

    /* ── AI / Insights accent ── */
    --color-ai-accent: #7B7DBA;
    --color-ai-accent-hover: #9092CE;
    --color-ai-accent-subtle: rgba(123, 125, 186, 0.20);
    /* Colourful gradient used for the Insights button border. */
    --color-ai-gradient: linear-gradient(120deg, #8b8ddb, #c084fc, #67e8f9);

    /* ── Loading Spinner ── */
    --color-spinner-grouped: #3A3D6A;
    --color-spinner-default: #2E3060;

    /* ── Skeleton (loading placeholders) ── */
    --color-skeleton-base: #2C3643;
    --color-skeleton-highlight: #36414F;

    /* ── Shadows ── */
    --shadow-sm: 0px 3px 2px -1px rgba(0,0,0,0.12), 0px 1px 1px -1px rgba(0,0,0,0.16);
    --shadow-md: 0px 4px 8px -4px rgba(0,0,0,0.32), 0px 4px 12px -2px rgba(0,0,0,0.36);
    --shadow-lg: 0 16px 40px rgba(0,0,0,0.44), 0 4px 14px rgba(0,0,0,0.30);
    --shadow-xs: 0px 0px 2px 0px rgba(0,0,0,0.35);
    --shadow-inset: inset 0px -1px 0px 0px rgba(0,0,0,0.24);
    --shadow-border: 0px 3px 8px 0px rgba(0,0,0,0.16);
    --shadow-dropdown: 0px 2px 8px 0px rgba(0,0,0,0.32);
    --shadow-tooltip: 0px 2px 4px 0px rgba(0,0,0,0.36);
    --shadow-floating-button: 0px 2px 6px rgba(0,0,0,0.24);
    --shadow-overlay: rgba(0, 0, 0, 0.5);

    /* ── Tooltip ── */
    --color-tooltip-bg: #FFFFFF;
    --color-tooltip-text: #000000;
    --color-tooltip-text-muted: #666666;
    --color-tooltip-section-bg: #F0F0F0;
    --color-tooltip-row-active-bg: #D0D0D0;
    --color-tooltip-divider: #CCCCCC;
    --color-tooltip-positive: #1E7F52;
    --color-tooltip-positive-bg: #E3F5EC;

    /* ── Calendar ── */
    --color-calendar-range-bg: #3A2A2E;
    --color-calendar-range-text: #E8E8E8;
    --color-calendar-selected-bg: #DF3D55;
    --color-calendar-selected-text: #FFFFFF;

    /* ── Pill ── */
    --color-pill-selected-bg: #FFFFFF;
    --color-pill-selected-text: #1A1919;

    /* ── Grid ── */
    --color-grid-odd-row: #2A3440;
    --color-grid-odd-row-hover: #313B48;
    --color-grid-even-row-hover: #2C3643;
    --color-grid-border: #2C3643;
    --color-grid-border-alt: #36414F;
    --color-grid-border-row: #313B48;
    --color-grid-selected-row: #1E3A5C;
    --color-grid-header-text: #E0E0E0;
    --color-grid-cell-text: #CCCCCC;
    --color-grid-loading-overlay: rgba(0, 0, 0, 0.2);

    /* ── Badge ── */
    --color-badge-info-bg: #1A2040;
    --color-badge-info-border: #2A3560;
    --color-badge-warning-bg: #3A2E1A;
    --color-badge-warning-border: #5A4520;
    --color-badge-danger-bg: #3A1A1A;
    --color-badge-danger-border: #5A2A2A;
    --color-badge-success-bg: #1A3A1A;
    --color-badge-success-border: #2A4A2A;

    /* Badge type-specific colors */
    --color-badge-blue-bg: #1A2540;
    --color-badge-blue-border: #253560;
    --color-badge-blue-text: #6B8FFF;

    --color-badge-turquoise-bg: #1A2E30;
    --color-badge-turquoise-border: #204040;
    --color-badge-turquoise-text: #40C4B0;

    --color-badge-green-bg: #1A3020;
    --color-badge-green-border: #204528;
    --color-badge-green-text: #50C870;

    --color-badge-olive-bg: #283018;
    --color-badge-olive-border: #384520;
    --color-badge-olive-text: #90C840;

    --color-badge-orange-bg: #3A2E1A;
    --color-badge-orange-border: #5A4520;
    --color-badge-orange-text: #FFB060;

    --color-badge-yellow-bg: #302E18;
    --color-badge-yellow-border: #454020;
    --color-badge-yellow-text: #E0B040;

    --color-badge-red-bg: #3A1A1A;
    --color-badge-red-border: #5A2525;
    --color-badge-red-text: #FF6060;

    --color-badge-purple-bg: #201A40;
    --color-badge-purple-border: #302560;
    --color-badge-purple-text: #8B80FF;

    --color-badge-magenta-bg: #2E1A40;
    --color-badge-magenta-border: #402060;
    --color-badge-magenta-text: #C070FF;

    --color-badge-pink-bg: #3A1A2A;
    --color-badge-pink-border: #502040;
    --color-badge-pink-text: #FF60A0;

    /* ── Scorecard ── */
    --color-scorecard-green-desaturated: #1A3A1A;
    --color-scorecard-green: #1A3020;
    --color-scorecard-red-desaturated: #3A1A1A;
    --color-scorecard-red: #3A2020;
    --color-scorecard-amber: #3A2E1A;
    --color-scorecard-green-text: #50C870;
    --color-scorecard-amber-text: #E0B040;
    --color-scorecard-red-text: #FF6060;
    --color-scorecard-grey-text: #AAAAAA;
    --color-scorecard-black-text: #E8E8E8;
    --color-scorecard-green-desaturated-chart: #4CC990;
    --color-scorecard-green-chart: #50B880;
    --color-scorecard-red-desaturated-chart: #E07070;
    --color-scorecard-red-chart: #D06060;
    --color-scorecard-amber-chart: #F3C25F;

    /* ── User Icon (17 original avatar colors — dark variants) ── */
    --color-usericon-0-bg: #362E48;
    --color-usericon-0-text: #C8BDE0;
    --color-usericon-1-bg: #1E3545;
    --color-usericon-1-text: #A0D0E8;
    --color-usericon-2-bg: #3A3520;
    --color-usericon-2-text: #E8D480;
    --color-usericon-3-bg: #1E3828;
    --color-usericon-3-text: #A0D8AA;
    --color-usericon-4-bg: #243830;
    --color-usericon-4-text: #90B097;
    --color-usericon-5-bg: #3E2028;
    --color-usericon-5-text: #E8A0B0;
    --color-usericon-6-bg: #003840;
    --color-usericon-6-text: #50D8E8;
    --color-usericon-7-bg: #102850;
    --color-usericon-7-text: #5898FF;
    --color-usericon-8-bg: #2A2248;
    --color-usericon-8-text: #A090E0;
    --color-usericon-9-bg: #28303A;
    --color-usericon-9-text: #A8B0C0;
    --color-usericon-10-bg: #3E2820;
    --color-usericon-10-text: #FFA088;
    --color-usericon-11-bg: #3A3520;
    --color-usericon-11-text: #E8D070;
    --color-usericon-12-bg: #1A3830;
    --color-usericon-12-text: #70E0B0;
    --color-usericon-13-bg: #1A3538;
    --color-usericon-13-text: #70D8E8;
    --color-usericon-14-bg: #1A2E50;
    --color-usericon-14-text: #70A8FF;
    --color-usericon-15-bg: #2A2548;
    --color-usericon-15-text: #A898E0;
    --color-usericon-16-bg: #282E38;
    --color-usericon-16-text: #98A0B0;

    /* ── Avatar Colors ── */
    --color-avatar-1-bg: #2A2540;
    --color-avatar-1-text: #9B8FD0;
    --color-avatar-2-bg: #1A2A45;
    --color-avatar-2-text: #6B9FE0;
    --color-avatar-3-bg: #352A1A;
    --color-avatar-3-text: #D09540;
    --color-avatar-4-bg: #252830;
    --color-avatar-4-text: #8890A5;
    --color-avatar-5-bg: #1A3530;
    --color-avatar-5-text: #50B898;
    --color-avatar-6-bg: #1A3038;
    --color-avatar-6-text: #50A8C0;
    --color-avatar-7-bg: #351A28;
    --color-avatar-7-text: #D07090;
    --color-avatar-8-bg: #351A1A;
    --color-avatar-8-text: #E06060;

    /* ── Colors2 Grey Scale ── */
    --color-grey-1: #E0E0E0;
    --color-grey-2: #AAAAAA;
    --color-grey-3: #888888;
    --color-grey-4: #777777;
    --color-grey-5: #666666;
    --color-grey-7: #3A3A3A;
    --color-grey-8: #2E2E2E;
    --color-grey-9: #222228;

    /* ── Legacy Secondary Colors ── */
    --color-info-v2: #6B8FFF;
    --color-success-v2: #6FBD84;

    /* ── Legacy Accent Colors ── */
    --color-gun-metal: #CCCCCC;
    --color-logan: #777788;
    --color-navy: #4090FF;
    --color-dodger-blue: #50B8FF;
    --color-shuttle-grey: #999999;
    --color-shady-lady: #777777;

    /* ── Legacy Border / Stroke ── */
    --color-gainsboro: #3A3A3A;
    --color-saturated-zircon: #333338;
    --color-stroke-light: #333340;
    --color-teams-close: #3A3A40;

    /* ── Legacy Navigation ── */
    --color-nav-bg: #1A1520;
    --color-nav-text: #C0B0D0;
    --color-nav-icon: #8878A0;
    --color-nav-text-light: #A99CC0;
    --color-nav-grey: #2E2E30;

    /* ── Studio canvas ── */
    /* The entity Studio previews as, among the top bar controls */
    --color-studio-preview-chip-bg: rgba(123, 125, 186, 0.16);
    --color-studio-preview-chip-border: rgba(123, 125, 186, 0.5);
    /* Panel edges and bars, the same line as the top bar's (--shadow-inset) */
    --color-studio-divider: rgba(0, 0, 0, 0.24);
    --color-studio-cell-outline: rgba(255, 255, 255, 0.14);
    --color-studio-canvas-border: rgba(255, 255, 255, 0.06);
    /* Around the canvas's frame, a shade under it */
    --color-studio-canvas-surround: #15191E;
    --color-studio-toggle-track: #0F1216;
    --color-studio-grid-dot: rgba(255, 255, 255, 0.12);
    --color-studio-drop-target: rgba(255, 255, 255, 0.1);
    /* Where a block being dragged onto the grid would land */
    --color-studio-drop-preview-bg: rgba(142, 162, 255, 0.08);
    --color-studio-drop-preview-border: #8EA2FF;
    --color-studio-drop-preview-halo: rgba(142, 162, 255, 0.16);
    /* A Grouped block's dashed "+" spots a drag could go in: filled, the one
       it's over more so, with a stronger "+" (lighter, on the dark canvas) */
    --color-studio-drop-spot-bg: rgba(142, 162, 255, 0.18);
    --color-studio-drop-spot-bg-active: rgba(142, 162, 255, 0.36);
    --color-studio-drop-spot-icon: #C3CEFF;
    /* The shimmer across a block just added from the library */
    --color-studio-reveal-shimmer: rgba(142, 162, 255, 0.18);
    /* A selected block's outline and resize handles, and their hover hint */
    --color-studio-selection: #8EA2FF;
    --color-studio-selection-subtle: rgba(142, 162, 255, 0.4);
    /* The drag tab over a hovered block that isn't selected */
    --color-studio-drag-chip-bg: #6E6E6E;
    --color-studio-drag-chip-text: #FFFFFF;
    /* A Grouped block's row under the pointer */
    --color-studio-row-hover-bg: rgba(142, 162, 255, 0.05);
    /* The gadget type palette's two-tone icons, and their emptiest heatmap cell */
    --color-studio-vis-icon-accent: #8EA2FF;
    --color-studio-vis-icon-soft: #3B4778;
    --color-studio-vis-icon-muted: #3A414C;
    /* The library's gadget types and metrics, raised like chips: shaded down
       to a darker lower edge, a sheen along the top and a shadow that lifts
       on hover */
    --color-studio-chip-shade: #1A1F25;
    --color-studio-chip-edge: #12161A;
    --color-studio-chip-sheen: rgba(255, 255, 255, 0.06);
    --shadow-studio-chip: 0 1px 2px rgba(0, 0, 0, 0.4);
    --shadow-studio-chip-lifted: 0 4px 10px -2px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.4);
    /* A Studio block held while it's dragged */
    --shadow-studio-block-lifted: 0 16px 32px -8px rgba(0, 0, 0, 0.6), 0 4px 12px -2px rgba(0, 0, 0, 0.45);
    /* The settings panel's section titles: a slate grey overline */
    --color-studio-section-title: #A3A8B4;
    /* A setting the gadget still needs (e.g. its first metric): a tinted
       drop zone with a dashed edge */
    --color-studio-required-bg: rgba(142, 162, 255, 0.08);
    --color-studio-required-bg-hover: rgba(142, 162, 255, 0.14);
    --color-studio-required-border: rgba(142, 162, 255, 0.4);
    --color-studio-required-text: #8EA2FF;
    /* A settings section appearing in place (e.g. a chart's dimensions,
       once it has a metric): pulsed once, so the eye lands on it */
    --color-studio-section-reveal: rgba(142, 162, 255, 0.2);
    /* A panel's link out to a modal (e.g. View Metric Library): a full
       width bar, shaded darker on hover */
    --color-studio-modal-link-bg-hover: rgba(0, 0, 0, 0.28);
    /* The settings panel's Advanced button: link blue, tinted blue on hover */
    --color-studio-advanced-bg-hover: rgba(107, 143, 255, 0.14);
    --color-studio-advanced-bg-active: rgba(107, 143, 255, 0.22);
    --color-studio-advanced-focus: rgba(107, 143, 255, 0.5);

    /* ── Driver Leaderboard wallboard ──
       For wallboards with Dark mode on: the same roles on the dark surfaces,
       with brighter medals and up/down colours to keep their contrast. */
    --color-leaderboard-surface: #252E39;
    --color-leaderboard-header-bg: #2C3643;
    --color-leaderboard-border: #36414F;
    --color-leaderboard-row-border: #313B48;
    --color-leaderboard-ink: #E8E8E8;
    --color-leaderboard-header-label: #CCCCCC;
    --color-leaderboard-muted: #999999;
    --color-leaderboard-medal-gold: #D9A55A;
    --color-leaderboard-medal-silver: #A8ADB4;
    --color-leaderboard-medal-bronze: #D07A66;
    --color-leaderboard-tint-gold: #3A3226;
    --color-leaderboard-tint-silver: #2F3742;
    --color-leaderboard-tint-bronze: #3A2C2A;
    --color-leaderboard-up: #4CC27A;
    --color-leaderboard-down: #F0606E;
    --color-leaderboard-down-arrow: #E66B6B;
    --color-leaderboard-new: #6B8FFF;
    --color-leaderboard-quiet: #555555;
    --color-leaderboard-progress: #E8E8E8;
    --color-leaderboard-freshness: #888888;
    --color-leaderboard-secondary: #B0B0B0;
    --color-leaderboard-row-stripe: #28313C;
  }
`;

// TypeScript-friendly color constants for direct JS usage
export const ChartColors = [
  '#a7abea',
  '#EAB16D',
  '#7AB1D2',
  '#EAD659',
  '#6DDC8F',
  '#EA962E',
  '#A9D346',
  '#EA60A8',
  '#B0C95C',
  '#C04FEA',
  '#EA3E43',
  '#194B3D',
  '#191939',
  '#1F5D4F',
  '#224C19',
  '#5C1F35',
  '#C55259',
  '#E05A00',
  '#5364A7',
  '#295D1F',
  '#55C757',
  '#1BBE95',
  '#56C7BE',
  '#673C18',
  '#141E95',
  '#1F306E',
  '#4A1942',
  '#6455C6',
  '#C654A4',
  '#5D501F',
  '#C7B656',
  '#48181B',
  '#BA2020',
  '#472518',
  '#5D251F',
  '#FFA70B',
  '#F0D632',
  // Macaron, Pastel, Dusk and Meadow palettes (chart-38 to chart-57)
  '#2EC7C9',
  '#B6A2DE',
  '#5AB1EF',
  '#FFB980',
  '#D87A80',
  '#FC97AF',
  '#87F7CF',
  '#F7F494',
  '#72CCFF',
  '#F7C5A0',
  '#516B91',
  '#59C4E6',
  '#EDAFDA',
  '#93B7E3',
  '#A5E7F0',
  '#3FB1E3',
  '#6BE6C1',
  '#626C91',
  '#A0A7E6',
  '#C4EBAD',
];

export interface AvatarColor {
  background: string;
  text: string;
}

export const AvatarColors: AvatarColor[] = [
  { background: '#E7E4F7', text: '#6859B5' }, // Index 0 (was Colors2.AvatarColors['1'])
  { background: '#D4E6FF', text: '#1D63Be' }, // Index 1 (was Colors2.AvatarColors['2'])
  { background: '#FFF3E3', text: '#9B5C03' }, // Index 2 (was Colors2.AvatarColors['3'])
  { background: '#E7E9ED', text: '#515D72' }, // Index 3 (was Colors2.AvatarColors['4'])
  { background: '#DDF8F1', text: '#06795C' }, // Index 4 (was Colors2.AvatarColors['5'])
  { background: '#DAF5FB', text: '#147286' }, // Index 5 (was Colors2.AvatarColors['6'])
  { background: '#FEEEF4', text: '#AB4367' }, // Index 6 (was Colors2.AvatarColors['7'])
  { background: '#FDE9E9', text: '#C9181E' }, // Index 7 (was Colors2.AvatarColors['8'])
];

export default cssTokens;
